import { Component, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
  ValidatorFn,
  FormControl,
} from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import {
  ComponentDetail,
  PreconfiguredSub,
} from '../../../interfaces/preconfiguredSubs.interface';
import { PreconfigSubServiceService } from '../../../Services/preconfig-sub-service.service';
import { AddressServiceService } from '../../../Services/address-service.service';
import { ChargifyFormComponent } from '../../chargify-form/chargify-form.component';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { PurchaseConfirmationDialogComponent } from '../purchase-confirmation-dialog/purchase-confirmation-dialog.component';
import { MaxioCustomerService } from '../../../Services/maxio-customer.service';
import { SubProductsService } from '../../../Services/sub-products.service';
import { PreviewProformaInvoiceService } from '../../../Services/preview-proforma-invoice.service';

@Component({
  selector: 'app-prop-manager-stepper',
  templateUrl: './prop-manager-stepper.component.html',
  styleUrls: ['./prop-manager-stepper.component.scss'],
  standalone: false,
})
export class PropManagerStepperComponent {
  subscriptionData: PreconfiguredSub | null = null;
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild(ChargifyFormComponent)
  chargifyFormComponent!: ChargifyFormComponent;
  chargifyFormValid: boolean = false;
  isLinear = true;
  customerInfoGroup!: FormGroup;
  paymentGroup!: FormGroup;
  usStates: { name: string; abbreviation: string }[] = [];
  canadaProvinces: { name: string; abbreviation: string }[] = [];
  usePropertyDetails: boolean = false;
  chargifyToken: string = '';
  chargifyDetails = {
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  };
  termsAccepted: boolean = false;
  achTermsAccepted: boolean = false;
  isAch: boolean = false;
  subscriptions: Subscription = new Subscription();
  achAgreement: any = null;
  previewData: any = null;
  lineItems: any[] = [];
  subtotal: number = 0;
  totalTax: number = 0;
  total: number = 0;
  confirmationResponse: any = null;
  maxioErrors: string[] = [];
  isPurchaseCompleted = new FormControl(false);
  billingDate: string | null = null;
  addressSuggestions: any[] = []; // Store the suggested addresses
  selectedAddress: string = '';
  billingDetailsGroup!: FormGroup;
  validAddressSelected: boolean = false;
  invoicePreviewUrl: string | null = null;
  loadingInvoicePreview = false;
  invoicePreviewError: string | null = null;

  constructor(
    readonly preconfigSubService: PreconfigSubServiceService,
    private readonly route: ActivatedRoute,
    readonly formBuilder: FormBuilder,
    readonly addressService: AddressServiceService,
    readonly dialog: MatDialog,
    readonly backend: BackendAccessService,
    readonly loading: LoadingService,
    readonly maxioCustomerService: MaxioCustomerService,
    private readonly subProducts: SubProductsService,
    private readonly previewInvoiceSvc: PreviewProformaInvoiceService
  ) {}
  /* istanbul ignore next */
  ngOnInit() {
    // Fetch the recordId from the route
    const recordId = this.route.snapshot.paramMap.get('recordId');

    if (recordId) {
      // Fetch the subscription for the given recordId
      const subscription =
        this.preconfigSubService.getSubscriptionById(recordId);

      if (subscription) {
        this.subscriptionData = subscription; // Set the subscription data
        console.log('Loaded subscription:', subscription);
      } else {
        console.error('No subscription found for recordId:', recordId);
      }
    }

    // Subscribe to changes in the subscription data (if needed for updates)
    this.subscriptions.add(
      this.preconfigSubService.getAllSubscriptions().subscribe((data) => {
        console.log('Updated subscription data:', data);
      })
    );

    // Fetch US states (existing functionality)
    this.usStates = this.addressService.getUSStates();

    // Initialize forms (existing functionality)
    this.initializeForms();
  }

  /* istanbul ignore next */
  public initializeForms(): void {
    this.customerInfoGroup = this.formBuilder.group({
      property_name: ['', Validators.required],
      property_address: ['', Validators.required],
      property_city: ['', [Validators.required, alphabeticValidator()]],
      property_state: ['', Validators.required],
      property_zip: ['', [Validators.required, zipCodeValidator()]],
      property_country: ['US'],
      property_manager_f_name: ['', Validators.required],
      property_manager_l_name: ['', Validators.required],
      property_manager_email: ['', [Validators.required, strictEmailValidator]],
      property_manager_phone: ['', [cleanPhoneNumberValidator()]],
    });

    this.billingDetailsGroup = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      address: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
      country: ['US'],
    });
  }
  /* istanbul ignore next */
  onAddressInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const address = inputElement.value;

    this.validAddressSelected = false;

    if (address.length > 3) {
      this.fetchAddressSuggestions(address);
    }
  }
  /* istanbul ignore next */
  async onPreviewInvoiceClick(): Promise<void> {
    if (this.loadingInvoicePreview) return;

    if (this.invoicePreviewUrl) {
      this.previewInvoiceSvc.openInvoiceInNewTab(this.invoicePreviewUrl);
      return;
    }

    const payload = this.createPreviewPayload();
    if (!payload) {
      this.invoicePreviewError = 'Unable to build invoice payload.';
      return;
    }

    this.loadingInvoicePreview = true;
    this.invoicePreviewError = null;

    try {
      const publicUrl = await this.previewInvoiceSvc.getPublicUrl(payload);
      this.invoicePreviewUrl = publicUrl;
      this.previewInvoiceSvc.openInvoiceInNewTab(publicUrl);
    } catch (err: any) {
      console.error('Invoice preview failed', err);
      this.invoicePreviewError =
        (err?.message as string) ??
        'Failed to generate invoice preview. Please try again.';
    } finally {
      this.loadingInvoicePreview = false;
    }
  }

  /* istanbul ignore next */
  fetchAddressSuggestions(address: string): void {
    this.addressService.fetchFilteredAddressSuggestions(address).subscribe({
      next: (filteredSuggestions) => {
        this.addressSuggestions = filteredSuggestions;
      },
      error: (error) => {
        console.error('Failed to fetch address suggestions:', error);
      },
    });
  }
  /* istanbul ignore next */
  selectSuggestion(suggestion: any): void {
    if (this.addressService.isValidStreetAddress(suggestion.address.freeformAddress)) {
      this.updateFormWithAddress(suggestion.address);
      this.addressSuggestions = [];
      this.validAddressSelected = true;
    } else {
      console.error(
        'Invalid address selected:',
        suggestion.address.freeformAddress
      );
    }
  }
  /* istanbul ignore next */
  enterManualAddress(): void {
    this.addressSuggestions = [];
    this.validAddressSelected = true;
  }
  /* istanbul ignore next */
  updateFormWithAddress(address: any): void {
    this.customerInfoGroup.patchValue({
      property_address: `${address.streetNumber} ${address.streetName}`, // Street address
      property_city: address.municipality, // City
      property_state: address.countrySubdivisionCode, // State (abbreviation)
      property_zip: address.postalCode, // ZIP code
      property_country: address.countryCode, // Country code
    });
  }

  /* istanbul ignore next */
  copyPropertyDetailsToChargify() {
    if (this.usePropertyDetails) {
      const propertyDetails = this.customerInfoGroup.value;
      this.billingDetailsGroup.setValue({
        firstName: propertyDetails.property_manager_f_name,
        lastName: propertyDetails.property_manager_l_name,
        address: propertyDetails.property_address,
        city: propertyDetails.property_city,
        state: propertyDetails.property_state,
        zip: propertyDetails.property_zip,
        country: propertyDetails.property_country,
      });
      this.billingDetailsGroup.disable(); // Disable inputs
    } else {
      this.billingDetailsGroup.reset({
        country: 'US', // Set default country value
      });
      this.billingDetailsGroup.enable(); // Enable inputs
    }
    this.chargifyFormComponent.clearPaymentError();
  }

  /* istanbul ignore next */
  shouldShowNextButton(): boolean {
    return (
      (this.stepper?.selectedIndex ?? this.getStepCount()) <
      this.getStepCount() - 1
    );
  }

  /* istanbul ignore next */
  shouldShowSaveButton(): boolean {
    return (this.stepper?.selectedIndex ?? -1) === this.getStepCount() - 1;
  }

  /* istanbul ignore next */
  private getStepCount(): number {
    return this.stepper?.steps?.length || 0;
  }

  /* istanbul ignore next */
  async goToNextStep() {
    const currentStep = this.stepper.selectedIndex;

    // Step 0: customer info
    if (currentStep === 0) {
      if (this.customerInfoGroup.valid) {
        this.stepper.next();
      } else {
        console.error('Customer Info form is invalid.');
      }
      return;
    }

    if (currentStep === 1) {
      if (this.isAchSelected() && !this.achTermsAccepted) {
        console.warn('User must accept ACH terms before proceeding.');

        return;
      }

      const isChargifyValid = await this.validateChargifyForm();
      if (!isChargifyValid || !this.chargifyToken) {
        console.error('Chargify validation failed.');
        return;
      }

      await this.previewSubscription();

      if (this.maxioErrors?.length > 0) {
        console.error('Maxio errors present, cannot proceed.');
        return;
      }

      return;
    }

    this.stepper.next();
  }

  /* istanbul ignore next */
  isAchSelected(): boolean {
    return !!this.chargifyFormComponent?.isAch;
  }

  /* istanbul ignore next */
  validateChargifyForm(): Promise<boolean> {
    return new Promise((resolve) => {
      // Clear previous subscriptions to avoid duplicates
      this.subscriptions.add(
        this.chargifyFormComponent.validationSuccess.subscribe(() => {
          console.log('Chargify form validation successful.');
          this.chargifyFormValid = true;
          this.chargifyToken = this.chargifyFormComponent.token;
          resolve(true);
        })
      );

      this.subscriptions.add(
        this.chargifyFormComponent.validationFailure.subscribe(() => {
          console.error('Chargify form validation failed.');
          this.chargifyFormValid = false;
          resolve(false);
        })
      );

      // Trigger validation
      this.chargifyFormComponent.validateChargifyForm();
    });
  }

  /* istanbul ignore next */
  goToPreviousStep() {
    if (this.stepper) {
      this.stepper.previous();
    }
  }
  /* istanbul ignore next */
  goToStep(stepIndex: number) {
    if (this.stepper) {
      this.stepper.selectedIndex = stepIndex; // Navigate to the specified step
    }
  }
  /* istanbul ignore next */
  onChargifyValidationSuccess() {
    console.log('Chargify form validation successful.');
    this.chargifyFormValid = true;
    this.chargifyToken = this.chargifyFormComponent.token; // Store the token
  }
  /* istanbul ignore next */
  onChargifyValidationFailure() {
    console.error('Chargify form validation failed.');
    this.chargifyFormValid = false;
  }
  /* istanbul ignore next */
  onAchTermsAcceptedChange(status: boolean) {
    this.achTermsAccepted = status;
    console.log('ACH Terms Accepted:', this.achTermsAccepted);
  }
  /* istanbul ignore next */
  onAchAgreementComplete(agreement: any) {
    this.achAgreement = agreement;
    console.log('ACH Agreement Data:', this.achAgreement);
  }

  /* istanbul ignore next */
  isCurrentStepValid(): boolean {
    if (!this.stepper) {
      console.log('Stepper is not initialized');
      return false;
    }

    const currentStep = this.stepper.selectedIndex;

    if (currentStep === 0) {
      return this.customerInfoGroup.valid;
    } else if (currentStep === 1) {
      if (this.isAchSelected()) {
        return this.achTermsAccepted;
      }
      return true;
    }
    return true;
  }

  /* istanbul ignore next */
  previewSubscription(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loading.show();
      console.log(
        'Purchasing subscription with payload:'
        // this.createPurchasePayload()
      );
      const payload = this.createPreviewPayload();
      console.log('Payload for purchase:', payload);
      this.backend.previewSubscription(payload).subscribe({
        next: (response) => {
          console.log('Preview response:', response);
          if (response?.maxioErrors?.length > 0) {
            console.error('Maxio Errors:', response.maxioErrors);
            this.maxioErrors = response.maxioErrors; // Store the errors
            this.loading.hide();
            resolve();
            return; // Prevent proceeding to the next step
          }
          this.previewData = response;
          const manifest =
            response?.subscriptionPreview?.currentBillingManifest;
          this.billingDate = manifest?.endDate;
          this.lineItems = manifest?.lineItems ?? [];
          this.subtotal = manifest?.subtotalInCents / 100 || 0;
          this.totalTax = manifest?.totalTaxInCents / 100 || 0;
          this.total = manifest?.totalInCents / 100 || 0;
          this.maxioErrors = [];
          this.loading.hide();
          this.stepper.next(); // Proceed to the next step after preview
          resolve();
        },
        error: (err) => {
          console.error('Preview error:', err);
          this.loading.hide();
          if (err instanceof Error) {
            reject(err);
          } else {
            reject(
              new Error(typeof err === 'string' ? err : JSON.stringify(err))
            );
          }
        },
      });
    });
  }

  /* istanbul ignore next */
  purchaseSubscription() {
    // Show loading indicator
    this.loading.show();

    // Get the recordId from the route
    const recordId = this.route.snapshot.paramMap.get('recordId');

    if (!recordId) {
      console.error('Record ID is missing!');
      this.loading.hide();
      return;
    }

    // Create the payload for the subscription
    const subPayload = this.createPreviewPayload();

    // Add recordId and roleCode to the payload
    const payloadWithRecordId = {
      ...subPayload,
      recordId: recordId,
      wholesalerUserEmail: null,
      roleCode: 'ZEND',
    };

    console.log('Purchasing subscription with payload:', payloadWithRecordId);

    // Call the backend service to create the subscription
    this.backend.createSubscription(payloadWithRecordId).subscribe({
      next: (response) => {
        console.log('Purchase response:', response);
        this.confirmationResponse = response;
        this.loading.hide();

        // Check if the response contains an error or if maxioSubId is null
        if (response.errorMessage || response.maxioSubId === null) {
          console.error('Subscription creation failed:', response.errorMessage);
          alert(
            'Failed to create subscription. Please check your details and try again.'
          );
          return; // Do not proceed to the next step
        }

        this.isPurchaseCompleted.setValue(true);

        // Proceed to the next step if no errors
        this.dialog.open(PurchaseConfirmationDialogComponent, {
          data: {
            productName:
              this.subscriptionData?.subscriptionData?.product_name ?? 'N/A',
            subscriptionId: response.maxioSubId,
          },
          disableClose: true,
        });
      },
      error: (error) => {
        console.error('Purchase error:', error);
        this.loading.hide();
        // Handle error response
        alert('Failed to create subscription. Please try again.');
      },
    });
  }

  /* istanbul ignore next */
  createPreviewPayload(): any {
    const customerInfo = this.customerInfoGroup.value; // Get the latest form values

    const existingCustomerId = this.maxioCustomerService.currentCustomerId;

    // Conditionally include customer_attributes
    const customerAttributes = existingCustomerId
      ? null
      : {
          first_name: customerInfo.property_manager_f_name ?? null,
          last_name: customerInfo.property_manager_l_name ?? null,
          email: customerInfo.property_manager_email ?? null,
          address: customerInfo.property_address ?? null,
          city: customerInfo.property_city ?? null,
          state: customerInfo.property_state ?? null,
          zip: customerInfo.property_zip ?? null,
          country: customerInfo.property_country ?? null,
          phone: customerInfo.property_manager_phone ?? null,
        };

    const subscriptionDetails: any = {
      product_handle:
        this.subscriptionData?.subscriptionData?.product_handle ?? null,
      product_id: this.subscriptionData?.subscriptionData?.product_id ?? null,
      product_price_point_id:
        this.subscriptionData?.subscriptionData?.product_price_point_id ?? null,
      custom_price: this.subscriptionData?.subscriptionData?.custom_price
        ? {
            price_in_cents: this.subscriptionData.subscriptionData.custom_price,
            interval: 'monthly',
            interval_unit: 'month',
          }
        : null,
      next_billing_at: null,
      coupon_code: this.subscriptionData?.subscriptionData?.coupon_code ?? null,
      customer_id: existingCustomerId ?? null, // Include existing customer_id or null
      customer_attributes: customerAttributes, // Include only if no existing customer_id
      credit_card_attributes: this.isAch
        ? null
        : { chargify_token: this.chargifyToken, payment_type: 'CreditCard' },
      bank_account_attributes: this.isAch
        ? { chargify_token: this.chargifyToken, payment_type: 'BankAccount' }
        : null,
      ach_agreement: this.isAch
        ? {
            agreement_terms: this.achAgreement?.agreement_terms ?? null,
            authorizer_first_name:
              this.achAgreement?.authorizer_first_name ?? null,
            authorizer_last_name:
              this.achAgreement?.authorizer_last_name ?? null,
            ip_address: this.achAgreement?.ip_address ?? null,
          }
        : null,
      components:
        this.subscriptionData?.subscriptionData?.components?.map(
          (component: ComponentDetail) => ({
            component_id: component.component_id ?? null,
            allocated_quantity: component.allocated_quantity ?? null,
            price_point_id: component.custom_price
              ? null
              : component.price_point_id ?? null,
            custom_price: component.custom_price ?? null,
          })
        ) ?? [],
      metafields: {
        property_name: customerInfo.property_name ?? null,
        property_address: customerInfo.property_address ?? null,
        property_city: customerInfo.property_city ?? null,
        property_state: customerInfo.property_state ?? null,
        property_zip: customerInfo.property_zip ?? null,
        property_country: customerInfo.property_country ?? 'US',
        property_manager_f_name: customerInfo.property_manager_f_name ?? null,
        property_manager_l_name: customerInfo.property_manager_l_name ?? null,
        property_manager_email: customerInfo.property_manager_email ?? null,
        property_manager_phone: customerInfo.property_manager_phone ?? null,
        projectType:
          this.subscriptionData?.subscriptionData?.metafields['projectType'] ??
          null,
        implementationStartDate:
          this.subscriptionData?.subscriptionData?.metafields[
            'implementationStartDate'
          ] ?? null,
        engageMigration:
          this.subscriptionData?.subscriptionData?.metafields[
            'engageMigration'
          ] ?? null,
        expectedOccupancyDate:
          this.subscriptionData?.subscriptionData?.metafields[
            'expectedOccupancyDate'
          ] ?? null,
        engageSiteName:
          this.subscriptionData?.subscriptionData?.metafields[
            'engageSiteName'
          ] ?? null,
        engageSiteId:
          this.subscriptionData?.subscriptionData?.metafields['engageSiteId'] ??
          null,
        expectedMigrationDate:
          this.subscriptionData?.subscriptionData?.metafields[
            'expectedMigrationDate'
          ] ?? null,
      },
    };

    return { subscriptionDetails };
  }

  /* istanbul ignore next */
  onPaymentMethodChange(isAch: boolean) {
    this.isAch = isAch; // Update the parent's isAch flag
    console.log('Payment method changed. isAch:', this.isAch);
  }
  /* istanbul ignore next */
  get isOneTimeProduct(): boolean {
    const prodId =
      this.subscriptionData?.subscriptionData?.product_id ??
      this.subscriptionData?.maxioProductId;
    return this.subProducts.isOneTimeProductId(prodId);
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    this.subscriptions.unsubscribe();
  }
}

/* istanbul ignore next */
export function cleanPhoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;
    if (!rawValue) {
      return null; // No value, no error
    }

    // Remove all non-numeric characters (e.g., spaces, hyphens, parentheses, plus signs, etc.)
    const cleanedValue = rawValue.replace(/\D/g, '');

    // Check if the cleaned value is exactly 10 digits
    const isValid = /^\d{10}$/.test(cleanedValue);

    if (!isValid) {
      return {
        invalidPhoneNumber: {
          requiredLength: 10,
          actualLength: cleanedValue.length,
        },
      };
    }

    return null; // Valid phone number
  };
}

/* istanbul ignore next */
export function numericValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const isValid = /^\d*$/.test(value);
    return isValid ? null : { nonNumeric: true };
  };
}
/* istanbul ignore next */
export function zipCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Define regex patterns for US and Canadian zip codes
    const usZipPattern = /^\d{5}$/; // Matches 5 digits or ZIP+4 format
    // const canadaZipPattern = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/; // Matches Canadian zip format

    // Check if the value matches either pattern
    const isValid = usZipPattern.test(value);

    // Return validation result
    return isValid ? null : { invalidZipCode: true };
  };
}
/* istanbul ignore next */
export function alphabeticValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Allow letters, spaces, and hyphens (optional)
    const isValid = /^[a-zA-Z\s-]+$/.test(value);

    return isValid ? null : { nonAlphabetic: true };
  };
}
/* istanbul ignore next */
export function simpleEmailValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;
  if (value && (value.indexOf('@') === -1 || value.indexOf('.') === -1)) {
    return { invalidEmail: true };
  }
  return null;
}
/* istanbul ignore next */
export function strictEmailValidator(
  control: AbstractControl
): ValidationErrors | null {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const value = control.value;
  if (value && !emailRegex.test(value)) {
    return { invalidEmail: true };
  }
  return null;
}
